// Swappable home cards and stat tiles (Kelly, 2026-10-24, mockup
// claude.ai/artifact/Nazw5VvuysHcz11oNM6ocJ). The home screen has two big card
// spots (left: Getting Started / Activity / My shift, right: Active Queue) and
// three stat tiles. Each DEVICE picks what fills them - saved in localStorage,
// not business settings, because a host stand iPad and the counter register
// want different things. Anyone can swap. A choice never turns a feature on or
// off; a card whose feature is off simply falls back to the default.

const STORE = 'pointbunny_home_cards';

// `main` is the left spot's own card: Getting Started until it retires, then
// Activity, or My shift for staff who don't see Activity (controller decides).
export const CARDS = [
  { key: 'main',           group: 'Orders and sales', label: 'Activity',             desc: 'Sales, clock-ins and stock alerts' },
  { key: 'queue',          group: 'Orders and sales', label: 'Active queue',         desc: 'Orders waiting to be made' },
  { key: 'salesToday',     group: 'Orders and sales', label: 'Sales today',          desc: 'Hour by hour, next to yesterday' },
  { key: 'salesWeek',      group: 'Orders and sales', label: 'Last 7 days',          desc: 'Daily sales, next to the week before' },
  { key: 'topSellers',     group: 'Orders and sales', label: 'Top sellers today',    desc: 'Your 5 best items so far' },
  { key: 'tables',         group: 'Tables',           label: 'Tables',               desc: 'Live floor: free, seated, needs cleaning' },
  { key: 'reservations',   group: 'Tables',           label: "Today's reservations", desc: 'Next bookings with a Seat button' },
  { key: 'waitlist',       group: 'Tables',           label: 'Waitlist',             desc: 'Parties waiting and how long' },
  { key: 'schedule',       group: 'Team',             label: "Today's schedule",     desc: 'Who works today, and who is late' },
  { key: 'team',           group: 'Team',             label: "Who's working",        desc: 'Who is clocked in right now' },
  { key: 'inbox',          group: 'Team',             label: 'Needs your OK',        desc: 'Swaps, time off and orders to review' },
  { key: 'lowStock',       group: 'Stock',            label: 'Low stock',            desc: 'Items running out', pro: true },
  { key: 'purchaseOrders', group: 'Stock',            label: 'Purchase orders',      desc: 'Orders on the way and drafts', pro: true },
];

// sales / txns / serving are the original tiles (index.html); the rest are
// drawn by homeCardsView.js.
export const TILES = [
  { key: 'sales',       group: 'Sales',            label: "Today's sales" },
  { key: 'txns',        group: 'Sales',            label: 'Transactions' },
  { key: 'avgOrder',    group: 'Sales',            label: 'Average order' },
  { key: 'tips',        group: 'Sales',            label: 'Tips today' },
  { key: 'itemsSold',   group: 'Sales',            label: 'Items sold' },
  { key: 'lastWeek',    group: 'Sales',            label: 'vs. last week' },
  { key: 'expenses',    group: 'Sales',            label: 'Expenses today' },
  { key: 'serving',     group: 'Kitchen and team', label: 'Avg serving time' },
  { key: 'waiting',     group: 'Kitchen and team', label: 'Orders waiting' },
  { key: 'onShift',     group: 'Kitchen and team', label: 'On shift now' },
  { key: 'requests',    group: 'Kitchen and team', label: 'To review' },
  { key: 'freeTables',  group: 'Tables',           label: 'Free tables' },
  { key: 'openTabs',    group: 'Tables',           label: 'Open tabs' },
  { key: 'nextBooking', group: 'Tables',           label: 'Next booking' },
  { key: 'lowItems',    group: 'Stock',            label: 'Low stock' },
];

export const DEFAULT_CARDS = ['main', 'queue'];
export const DEFAULT_TILES = ['sales', 'txns', 'serving'];

const CARD_KEYS = new Set(CARDS.map((c) => c.key));
const TILE_KEYS = new Set(TILES.map((t) => t.key));

// Suggested per type of place. Anything unavailable falls back in resolve().
const SUGGESTED = {
  restaurant: { cards: ['tables', 'queue'], tiles: ['sales', 'openTabs', 'freeTables'] },
  bakery:     { cards: ['main', 'queue'], tiles: ['sales', 'txns', 'lowItems'] },
};

export const suggested = (placeType) => {
  const s = SUGGESTED[placeType];
  return { cards: [...(s?.cards ?? DEFAULT_CARDS)], tiles: [...(s?.tiles ?? DEFAULT_TILES)] };
};

const clean = (list, known, defaults) => {
  const out = Array.isArray(list) ? list.slice(0, defaults.length) : [];
  return defaults.map((d, i) => (known.has(out[i]) ? out[i] : d));
};

export const load = () => {
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem(STORE) || 'null'); } catch { /* bad JSON = defaults */ }
  return {
    cards: clean(saved?.cards, CARD_KEYS, DEFAULT_CARDS),
    tiles: clean(saved?.tiles, TILE_KEYS, DEFAULT_TILES),
  };
};

export const save = (prefs) => {
  try { localStorage.setItem(STORE, JSON.stringify({ cards: prefs.cards, tiles: prefs.tiles })); } catch { /* private mode */ }
};

// Put `key` in spot `slot` of `list`. If it already shows in another spot, the
// two trade places, so the same card or tile never shows twice.
export const pick = (list, slot, key) => {
  const next = [...list];
  const from = next.indexOf(key);
  if (from === slot) return next;
  if (from !== -1) next[from] = next[slot];
  next[slot] = key;
  return next;
};

// What actually shows: each saved choice if its feature is on, else that
// spot's default, else the first available key not already showing.
export const resolve = (list, defaults, isAvailable, allKeys) => {
  const out = [];
  list.forEach((key, i) => {
    const tries = [key, defaults[i], ...allKeys];
    out.push(tries.find((k) => isAvailable(k) && !out.includes(k)));
  });
  return out;
};

export const allCardKeys = CARDS.map((c) => c.key);
export const allTileKeys = TILES.map((t) => t.key);

// The swap button every card header carries (cards drawn by other views too).
export const SWAP_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 16V4m0 0L3 8m4-4 4 4M17 8v12m0 0 4-4m-4 4-4-4"/></svg>';
export const swapButton = (key) =>
  `<button type="button" class="home-swap-btn" data-swap-card="${key}" aria-label="Swap this card" title="Choose what shows here">${SWAP_ICON}</button>`;
